import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { authService } from "Service/Axios";
import { ButtonLoader } from "Components/ButtonLoader";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { Validator } from "Utils/validator";
import { CREATE_STAGE_URL, UPDATE_STAGE_URL } from "Constant/api";

export const CreateStage = ({
  show,
  toggle,
  selectedData,
  fetchList,
  stageList,
  projectList,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    global: "global",
    projectIds: [],
    stageName: "",
    sequenceNo: 1,
  });
  const [errMsg, setErrMsg] = useState({
    projectIds: "",
    stageName: "",
    sequenceNo: "",
  });

  const { global, projectIds, stageName, sequenceNo } = formData;

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "stageName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          stageName: t("common.messages.error.adminSettings.stageName.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          stageName: "",
        }));
      }
    }

    if (e.target.name === "sequenceNo") {
      if (!Validator.number(Number(e.target.value), 1, undefined)) {
        setErrMsg((prev) => ({
          ...prev,
          sequenceNo: t("common.messages.error.adminSettings.sequenceNo.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          sequenceNo: "",
        }));
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));
    if (name === "projectIds") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: t("common.messages.error.adminSettings.projectIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: "",
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedData) {
      setFormData({
        global: selectedData.global ? "global" : "project",
        projectIds: selectedData.projects?.map((project) => ({
          label: project.name,
          value: project.id,
        })),
        stageName: selectedData.name,
        sequenceNo: selectedData.sequenceNo,
      });
    }
  }, [selectedData, projectList]);

  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(stageName) &&
        Validator.number(Number(sequenceNo), 1, undefined)
      ) {
        if (global !== "global") {
          if (Validator.hasMultiple(projectIds)) {
            resolve(true);
          } else {
            setErrMsg((prev) => ({
              ...prev,
              projectIds: t(
                "common.messages.error.adminSettings.projectIds.blank"
              ),
            }));
            resolve(false);
          }
        } else {
          resolve(true);
        }
      } else {
        if (!Validator.text(stageName)) {
          setErrMsg((prev) => ({
            ...prev,
            stageName: t("common.messages.error.adminSettings.stageName.blank"),
          }));
        }
        if (!Validator.number(Number(sequenceNo), 1, undefined)) {
          setErrMsg((prev) => ({
            ...prev,
            sequenceNo: t(
              "common.messages.error.adminSettings.sequenceNo.blank"
            ),
          }));
        }
        resolve(false);
      }
    });
  };

  // On Submit
  const onSaveStage = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          global: global === "global",
          name: stageName,
          sequenceNo,
        };
        if (global === "project") {
          payload.projects = projectIds?.map((option) => option.value);
        }
        if (selectedData) {
          payload.gid = selectedData.gid;
          const response = await authService(UPDATE_STAGE_URL, "POST", payload);
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.updateStage")
            );
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await authService(CREATE_STAGE_URL, "POST", payload);
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.createStage")
            );
          } else {
            toast.error(response.message);
          }
        }
        setLoading(false);
        fetchList(projectIds);
        toggle();
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.adminSettings.createStage")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <div className="mb-1">
              <label htmlFor="global" className="form-label me-3">
                {t("common.label.global")}
              </label>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  id="global"
                  value="global"
                  checked={global === "global"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.yes")}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <label htmlFor="global" className="form-check-label"></label>
                <input
                  type="radio"
                  id="global"
                  value="project"
                  checked={global === "project"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.no")}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={`mb-4 ${errMsg.projectIds ? "error" : ""}`}>
          <label htmlFor="projectIds" className="form-label">
            {t("common.label.projects")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.projects")}
            options={projectList}
            value={projectIds}
            onChange={(selected) =>
              handleChangeMultiple(selected, "projectIds")
            }
            disabled={global === "global"}
          />
          {errMsg.projectIds && <p>{errMsg.projectIds}</p>}
        </div>
        <div className={`mb-4 ${errMsg.stageName ? "error" : ""}`}>
          <label htmlFor="stageName" className="form-label">
            {t("common.label.stage")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.stage")}
            id="stageName"
            name="stageName"
            value={stageName}
            onChange={handleChange}
          />
          {errMsg.stageName && <p>{errMsg.stageName}</p>}
        </div>
        <div className={`mb-4 ${errMsg.sequenceNo ? "error" : ""}`}>
          <label htmlFor="sequenceNo" className="form-label">
            {t("common.label.sequence")}
          </label>
          <input
            type="number"
            className="form-control"
            placeholder={t("common.placeHolder.sequence")}
            name="sequenceNo"
            value={sequenceNo}
            onChange={handleChange}
            disabled
          />
          {errMsg.sequenceNo && <p>{errMsg.sequenceNo}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveStage}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
